'use client';

/**
 * CompactActionGroup Component
 *
 * A minimalistic action button group for table rows and list items.
 * Provides consistent, space-efficient action icons with hover reveal.
 *
 * Design principles:
 * - Extra small icons (14px) for minimal visual weight
 * - Actions fade in on parent hover for cleaner UI
 * - Consistent spacing and alignment
 * - Standardized action types with built-in icons
 */

import ArchiveIcon from '@mui/icons-material/Archive';
import CloseIcon from '@mui/icons-material/Close';
import CopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import MoreIcon from '@mui/icons-material/MoreVert';
import OpenIcon from '@mui/icons-material/OpenInNew';
import PauseIcon from '@mui/icons-material/Pause';
import PlayIcon from '@mui/icons-material/PlayArrow';
import PreviewIcon from '@mui/icons-material/Preview';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import StarIcon from '@mui/icons-material/Star';
import ViewIcon from '@mui/icons-material/Visibility';
import { Box, type SxProps, type Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { createSvgIcon } from '@mui/material/utils';
import type { ReactElement, ReactNode } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { GlassIconButton, GlassIconButtonSize } from '../GlassIconButton';
import { GlassTooltip } from '../GlassTooltip';

// "Maximize" / expand-to-full-page icon — two L-bracketed arrows on the
// forward-slash diagonal (top-right + bottom-left), with deliberate gap in
// the center. Diverges from MUI's `OpenInFull` (which uses the `\` diagonal
// and arrows that meet near center). Exported so callers building their own
// expand affordance (e.g. inside a Slideover header where CompactActionGroup
// doesn't fit) can reach for the same icon — never `@mui/icons-material/OpenInFull`.
export const ExpandIcon = createSvgIcon(
  <path
    d="M14 4 H20 V10 M20 4 L14 10 M10 20 H4 V14 M4 20 L10 14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  />,
  'Maximize',
);

// ============================================================================
// Types
// ============================================================================

/**
 * Standard action types with built-in icons.
 *
 * Semantics for "view"-ish actions:
 * - `view`   → eye icon; "view this thing" (open detail, preview content)
 * - `preview`→ preview icon; "peek / quicklook"
 * - `expand` → open-in-full arrows; "expand this preview to its full-page view"
 *              (use inside a SplitPanelFooter when the action navigates from a
 *              split-panel preview to the full detail page)
 * - `open`   → open-in-new arrows; "open in new tab/window"
 */
export type StandardActionType =
  | 'view'
  | 'expand'
  | 'edit'
  | 'delete'
  | 'copy'
  | 'preview'
  | 'download'
  | 'share'
  | 'settings'
  | 'archive'
  | 'link'
  | 'open'
  | 'refresh'
  | 'play'
  | 'pause'
  | 'send'
  | 'star'
  | 'close'
  | 'more';

/**
 * Action configuration
 */
export interface CompactAction {
  /** Standard action type (provides icon) or custom */
  type: StandardActionType | 'custom';
  /** Custom icon (required if type is 'custom') */
  icon?: ReactNode;
  /** Tooltip text */
  tooltip?: string;
  /** Click handler */
  onClick: (event: React.MouseEvent) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Color - 'error' for destructive actions like delete */
  color?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  /** Hide this action */
  hidden?: boolean;
}

export interface CompactActionGroupProps {
  /** Array of actions to render */
  actions: CompactAction[];
  /**
   * Size of action buttons
   * @default 'xs'
   */
  size?: GlassIconButtonSize | 'xs' | 'small' | 'medium';
  /**
   * Gap between buttons in pixels
   * @default 2
   */
  gap?: number;
  /**
   * Show actions only on parent hover
   * @default false
   */
  revealOnHover?: boolean;
  /**
   * Opacity when not hovered (if revealOnHover is true)
   * @default 0.4
   */
  idleOpacity?: number;
  /** Additional sx props */
  sx?: SxProps<Theme>;
  /** Stop event propagation on clicks */
  stopPropagation?: boolean;
}

// ============================================================================
// Icon Mapping
// ============================================================================

const actionIcons: Record<StandardActionType, ReactElement> = {
  view: <ViewIcon />,
  expand: <ExpandIcon />,
  edit: <EditIcon />,
  delete: <DeleteIcon />,
  copy: <CopyIcon />,
  preview: <PreviewIcon />,
  download: <DownloadIcon />,
  share: <ShareIcon />,
  settings: <SettingsIcon />,
  archive: <ArchiveIcon />,
  link: <LinkIcon />,
  open: <OpenIcon />,
  refresh: <RefreshIcon />,
  play: <PlayIcon />,
  pause: <PauseIcon />,
  send: <SendIcon />,
  star: <StarIcon />,
  close: <CloseIcon />,
  more: <MoreIcon />,
};

const defaultTooltipKeys: Record<StandardActionType, string> = {
  view: 'common.view',
  expand: 'common.viewDetails',
  edit: 'common.edit',
  delete: 'common.delete',
  copy: 'common.copy',
  preview: 'common.preview',
  download: 'common.download',
  share: 'common.share',
  settings: 'common.settings',
  archive: 'common.archive',
  link: 'common.copyLink',
  open: 'common.open',
  refresh: 'common.refresh',
  play: 'common.play',
  pause: 'common.pause',
  send: 'common.send',
  star: 'common.star',
  close: 'common.close',
  more: 'common.more',
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledGroupProps {
  $revealOnHover: boolean;
  $idleOpacity: number;
  $gap: number;
}

const StyledGroup = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledGroupProps>(({ $revealOnHover, $idleOpacity, $gap }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: $gap,
  ...($revealOnHover && {
    opacity: $idleOpacity,
    transition: 'opacity 0.15s ease-in-out',
    // Parent hover reveals actions - works with any hoverable parent
    '.MuiTableRow-root:hover &, .MuiCard-root:hover &, .MuiListItem-root:hover &, [data-hover]:hover &': {
      opacity: 1,
    },
    '&:hover, &:focus-within': {
      opacity: 1,
    },
  }),
}));

// ============================================================================
// Component
// ============================================================================

export const CompactActionGroup = ({
  actions,
  size = GlassIconButtonSize.XS,
  gap = 2,
  revealOnHover = false,
  idleOpacity = 0.4,
  sx,
  stopPropagation = true,
}: CompactActionGroupProps): ReactElement => {
  const { t } = useI18n();

  const handleClick = (action: CompactAction) => (event: React.MouseEvent) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    action.onClick(event);
  };

  const visibleActions = actions.filter((a) => !a.hidden);

  return (
    <StyledGroup $revealOnHover={revealOnHover} $idleOpacity={idleOpacity} $gap={gap} sx={sx}>
      {visibleActions.map((action, index) => {
        const icon = action.type === 'custom' ? action.icon : actionIcons[action.type];
        const tooltip = action.tooltip ?? (action.type !== 'custom' ? t(defaultTooltipKeys[action.type]) : undefined);
        const color = action.color ?? (action.type === 'delete' ? 'error' : 'default');

        const button = (
          <GlassIconButton
            key={index}
            size={size}
            variant="standard"
            color={color}
            onClick={handleClick(action)}
            disabled={action.disabled}
          >
            {icon}
          </GlassIconButton>
        );

        if (tooltip) {
          return (
            <GlassTooltip key={index} title={tooltip} arrow placement="top">
              <span>{button}</span>
            </GlassTooltip>
          );
        }

        return button;
      })}
    </StyledGroup>
  );
};

CompactActionGroup.displayName = 'CompactActionGroup';

export default CompactActionGroup;
